import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { CheckCircle, Clock, Users, FileText } from 'lucide-react';
import { format } from 'date-fns';

const MeetingEnded = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const meetingId = searchParams.get('meetingId');
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMeetingDetails = useCallback(async () => {
    try {
      const response = await api.get(`/meetings/${meetingId}`);
      setMeetingInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch meeting details:', err);
      setError('Failed to load meeting details');
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    if (!meetingId) {
      setError('No meeting information available');
      setLoading(false);
      return;
    }

    fetchMeetingDetails();
  }, [meetingId, fetchMeetingDetails]);

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${theme.colors.primary}15 0%, ${theme.colors.secondary}15 100%)`,
      padding: '20px',
    },
    card: {
      maxWidth: '600px',
      width: '100%',
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px',
    },
    icon: {
      width: '80px',
      height: '80px',
      margin: '0 auto 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      backgroundColor: theme.colors.success + '20',
      color: theme.colors.success,
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      margin: '0 0 8px 0',
    },
    subtitle: {
      fontSize: '16px',
      color: theme.colors.textSecondary,
      margin: 0,
    },
    detailsSection: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    detailItem: {
      display: 'flex',
      gap: '12px',
      padding: '12px',
      backgroundColor: theme.colors.background,
      borderRadius: '8px',
      marginBottom: '8px',
      border: `1px solid ${theme.colors.border}`,
    },
    detailIcon: {
      display: 'flex',
      alignItems: 'center',
      color: theme.colors.primary,
      flexShrink: 0,
    },
    detailContent: {
      flex: 1,
    },
    detailLabel: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      marginBottom: '2px',
    },
    detailValue: {
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginTop: '32px',
    },
    primaryButton: {
      flex: 1,
    },
    secondaryButton: {
      flex: 1,
    },
    feedback: {
      marginTop: '20px',
      padding: '16px',
      backgroundColor: theme.colors.primary + '10',
      borderRadius: '8px',
      border: `1px solid ${theme.colors.primary}30`,
      textAlign: 'center',
    },
    feedbackText: {
      fontSize: '13px',
      color: theme.colors.text,
      margin: 0,
    },
  };

  const getBackUrl = () => {
    const paths = {
      ADMIN: '/admin/meetings',
      SPEAKER: '/speaker/meetings',
      USER: '/user/meetings',
    };
    return paths[user?.role] || '/';
  };

  const handleReturnToMeetings = () => {
    navigate(getBackUrl());
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Card style={styles.card}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: theme.colors.textSecondary }}>Loading meeting information...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !meetingInfo) {
    return (
      <div style={styles.container}>
        <Card style={styles.card}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: theme.colors.danger, fontWeight: '600' }}>
              {error || 'Meeting information not available'}
            </p>
            <Button 
              onClick={handleReturnToMeetings}
              style={{ marginTop: '20px' }}
            >
              Return to Meetings
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Card style={styles.card}>
        <div style={{ padding: '40px' }}>
          <div style={styles.header}>
            <div style={styles.icon}>
              <CheckCircle size={48} />
            </div>
            <h1 style={styles.title}>Meeting Ended</h1>
            <p style={styles.subtitle}>Thanks for attending!</p>
          </div>

          <div style={styles.detailsSection}>
            <h2 style={styles.sectionTitle}>📋 Meeting Summary</h2>

            <div style={styles.detailItem}>
              <div style={styles.detailIcon}>
                <FileText size={20} />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Meeting Title</div>
                <div style={styles.detailValue}>{meetingInfo.title}</div>
              </div>
            </div>

            <div style={styles.detailItem}>
              <div style={styles.detailIcon}>
                <Clock size={20} />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Date & Time</div>
                <div style={styles.detailValue}>
                  {format(new Date(meetingInfo.startTime), 'PPP p')}
                </div>
              </div>
            </div>

            {meetingInfo.speakers?.length > 0 && (
              <div style={styles.detailItem}>
                <div style={styles.detailIcon}>
                  <Users size={20} />
                </div>
                <div style={styles.detailContent}>
                  <div style={styles.detailLabel}>Speakers</div>
                  <div style={styles.detailValue}>
                    {meetingInfo.speakers.map(s => s.speaker.name).join(', ')}
                  </div>
                </div>
              </div>
            )}

            {meetingInfo.description && (
              <div style={styles.detailItem}>
                <div style={styles.detailIcon}>
                  <FileText size={20} />
                </div>
                <div style={styles.detailContent}>
                  <div style={styles.detailLabel}>Description</div>
                  <div style={{ ...styles.detailValue, maxHeight: '80px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {meetingInfo.description}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={styles.feedback}>
            <p style={styles.feedbackText}>
              💡 Your feedback helps us improve. We'd love to hear from you!
            </p>
          </div>

          <div style={styles.actionButtons}>
            <Button 
              onClick={handleReturnToMeetings}
              style={styles.primaryButton}
            >
              Back to Meetings
            </Button>
            <Button 
              variant="secondary"
              onClick={() => navigate('/')}
              style={styles.secondaryButton}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MeetingEnded;
